import { useEffect, useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../common/PageBreadCrumb";
import Input from "../form/input/InputField";
import AdminPageShell from "../admin/AdminPageShell";
import AdminCard from "../admin/AdminCard";
import AdminListToolbar from "../admin/AdminListToolbar";
import {
  adminAlertErrorClass,
  adminDangerLinkClass,
  adminEditLinkClass,
  adminTableActionsClass,
  adminTableClass,
  adminTableEmptyClass,
  adminTableHeadClass,
  adminTableTdClass,
  adminTableThClass,
  adminTableWrapperClass,
} from "../admin/adminStyles";

type SimpleCrudListProps<T extends { id: string }> = {
  title: string;
  apiPath: string;
  baseRoute: string;
  columns: { key: keyof T | string; label: string; render?: (row: T) => ReactNode }[];
  searchKeys?: (keyof T)[];
  addLabel?: string;
  description?: string;
};

export default function SimpleCrudList<T extends { id: string }>({
  title,
  apiPath,
  baseRoute,
  columns,
  searchKeys = [],
  addLabel = "Add",
  description,
}: SimpleCrudListProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiPath);
      let data: T[] = response.data;
      if (searchTerm && searchKeys.length) {
        const q = searchTerm.toLowerCase();
        data = data.filter((row) =>
          searchKeys.some((k) => String(row[k] ?? "").toLowerCase().includes(q))
        );
      }
      setItems(data);
    } catch {
      setError(`Failed to fetch ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axiosInstance.delete(`${apiPath}/${id}`);
      fetchItems();
    } catch {
      setError("Delete failed");
    }
  };

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle={title}
        description={description ?? `Manage ${title.toLowerCase()}`}
      />

      <AdminListToolbar addTo={`${baseRoute}/add`} addLabel={addLabel}>
        <Input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </AdminListToolbar>

      {error && <div className={adminAlertErrorClass}>{error}</div>}

      <AdminCard padding={false}>
        <div className={adminTableWrapperClass}>
          <table className={adminTableClass}>
            <thead className={adminTableHeadClass}>
              <tr>
                {columns.map((c) => (
                  <th key={String(c.key)} className={adminTableThClass}>
                    {c.label}
                  </th>
                ))}
                <th className={`${adminTableThClass} text-end`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className={adminTableEmptyClass}>
                    Loading...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className={adminTableEmptyClass}>
                    No records
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.02]">
                    {columns.map((c) => (
                      <td key={String(c.key)} className={adminTableTdClass}>
                        {c.render
                          ? c.render(row)
                          : String((row as Record<string, unknown>)[c.key as string] ?? "")}
                      </td>
                    ))}
                    <td className={`${adminTableTdClass} text-end`}>
                      <div className={adminTableActionsClass}>
                        <Link to={`${baseRoute}/edit/${row.id}`} className={adminEditLinkClass}>
                          Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(row.id)}
                          className={adminDangerLinkClass}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </AdminPageShell>
  );
}
